import React, { useMemo, useState } from 'react';
import { Button } from 'antd';
import { Product, setWholeCart } from '../../ShoppingCartSliceV2';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../store/store';
import { Link } from 'react-router-dom';
import {
  packageLink,
  dynamicPackageLink,
  dynamicPackageEventsLink,
} from '../../../../utils/LinkGenerator';
import './shoppingCartPackage.less';
import { MainProductType } from '../../interfaces';
import { isEmpty } from 'lodash';
import FlightCard2 from '../../../SearchDatePicker/widgets/SearchResults/FlightCard2';
import ProductPreviewComponent from '../../widgets/ProductPreviewComponent';
import useHandleManageBaggage from '../../hooks/useHandleManageBaggage';
import BaggageManagement from '@skygroup/shared/components/baggageManagment/baggageManagment';

interface Props {
  ProductRenderer: ({
    product,
    identifier,
    transfer_type,
  }: {
    product: Product;
    identifier?: string;
    transfer_type?: string;
  }) => JSX.Element | null;
  subProducts: Product[];
  product: Product;
  identifier?: string;
  showProductPrice?: boolean;
  mainProductType?: MainProductType.DynamicPackage | MainProductType.Package;
}

const ShoppingCartPackage = ({
  subProducts,
  product,
  ProductRenderer,
  identifier,
  showProductPrice = true,
  mainProductType = MainProductType.Package,
}: Props) => {
  const isDynamicPackageSearchWithEvents = useMemo(() => {
    return subProducts?.some(
      (subProduct) => subProduct.type === MainProductType.Event
    );
  }, [subProducts]);

  const {
    shoppingCartTranslations: t,
    manageBaggage,
    searchDatePicker: searchDatePicker_t,
  } = useSelector((data: RootState) => data.config.translations);
  const { config, direction } = useSelector((data: RootState) => data.config);
  const { ShoppingCartV2 } = useSelector(
    (state: RootState) => state.shoppingCartV2
  );
  const subProductFlights = subProducts?.filter((subProduct) => {
    return subProduct.type === MainProductType.Flight;
  });
  const flightFromSubProducts = subProductFlights?.[0]?.subProducts;
  const flightBackSubProducts = subProductFlights?.[1]?.subProducts;

  const dispatch = useDispatch();

  const subProductWithoutFlights = subProducts?.filter((subProduct) => {
    return subProduct.type !== MainProductType.Flight;
  });

  const flight = isEmpty(subProductFlights)
    ? false
    : {
        ...subProductFlights[0].product_data,
        ...subProductFlights[1].product_data,
      };

  const [isManageBaggageOpen, setIsManageBaggageOpen] = useState(false);

  const {
    handleAddBaggage,
    flightBackBaggage,
    flightFromBaggage,
    isRoundTrip,
    isShowManageBaggageButton,
  } = useHandleManageBaggage({ product });

  const constructDynamicPackageLink = (product: Product) => {
    if (
      !product.identifier ||
      mainProductType !== MainProductType.DynamicPackage
    ) {
      return '';
    }
    const [h_id, f_id] = product.identifier.split('::');

    const eTicketIdentifier = product.subProducts?.find(
      (subProduct) => subProduct.type === MainProductType.Event
    )?.identifier;
    if (eTicketIdentifier) {
      return dynamicPackageEventsLink({
        h_id,
        f_id,
        eTicket: eTicketIdentifier,
      });
    }
    return dynamicPackageLink({ h_id, f_id });
  };

  // const getLabel = useMemo(() => {
  //   if (
  //     mainProductType === MainProductType.DynamicPackage &&
  //     isDynamicPackageSearchWithEvents
  //   ) {
  //     return searchDatePicker_t.hotel_plus_flight_events;
  //   } else if (mainProductType === MainProductType.DynamicPackage) {
  //     return searchDatePicker_t.hotel_plus_flight;
  //   } else {
  //     return t.package;
  //   }

  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [isDynamicPackageSearchWithEvents, mainProductType]);

  return (
    <div className="shopping-cart-package" key={product?.identifier}>
      {/* <Link to={packageLink(product)} className="link">
        <b>{t.package}</b>
      </Link> */}
      <ProductPreviewComponent
        productPath={
          mainProductType === MainProductType.DynamicPackage
            ? constructDynamicPackageLink(product)
            : packageLink(product)
        }
        label={mainProductType === MainProductType.DynamicPackage ? searchDatePicker_t.hotel_plus_flight : t.package}
        labelClassName={'link'}
        remarkTooltipText={t.dynamicPackageTitleRemark}
      />

      {mainProductType !== MainProductType.DynamicPackage && (
        <div className="package-general-info">
          {product.product_data.dates && (
            <>
              <b>{t.dates}</b>
              <div className="title">
                {`${moment(product.product_data.dates.start * 1000)
                  .utc()
                  .format(config?.siteConfig?.dateFormat)}
            -
            ${moment(product.product_data.dates.end * 1000)
              .utc()
              .format(config?.siteConfig?.dateFormat)}`}
              </div>
            </>
          )}

          <b>{t.hotelName}</b>
          <div className="title">{product.product_data.hotel.name}</div>
        </div>
      )}

      {subProductWithoutFlights.map((subProduct: Product) => (
        <React.Fragment key={subProduct?.identifier || subProduct?.name}>
          {ProductRenderer({
            product: subProduct,
            identifier,
            transfer_type: product?.product_data?.transfer_type,
          })}
        </React.Fragment>
      ))}
      {mainProductType !== MainProductType.DynamicPackage && (
        <b className="product-title">
          {t?.flights}{' '}
          {isShowManageBaggageButton && (
            <Button
              onClick={() => setIsManageBaggageOpen(true)}
              className="btn-style-4"
            >
              {t.manageBaggage}
            </Button>
          )}
        </b>
      )}

      {isManageBaggageOpen && (
        <BaggageManagement
          guests={ShoppingCartV2.guests}
          onChange={handleAddBaggage}
          flightBackBaggage={flightBackBaggage}
          flightFromBaggage={flightFromBaggage}
          mainProduct={product}
          shoppingCart={ShoppingCartV2}
          onClose={(cart: any) => {
            dispatch(setWholeCart(cart));
          }}
          isManageBaggageOpen={isManageBaggageOpen}
          setIsManageBaggageOpen={setIsManageBaggageOpen}
          onSave={() => setIsManageBaggageOpen(false)}
          isRoundTrip={isRoundTrip}
          t={manageBaggage}
          direction={direction}
        />
      )}

      {mainProductType !== MainProductType.DynamicPackage && (
        <FlightCard2
          flightFromSubProducts={flightFromSubProducts}
          flightBackSubProducts={flightBackSubProducts}
          flightData={flight}
          isShowFlightButton={false}
          handlePurchase={() => {}}
          isMinimized
          showProductPrice={showProductPrice}
        />
      )}
    </div>
  );
};

export default ShoppingCartPackage;
